import React, { ReactNode } from 'react'
import classnames from 'classnames'

type Props = {
  type: 'radio' | 'checkbox'
  name: string
  disabled?: boolean
  checked?: boolean
  id: string
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  onClick?: React.MouseEventHandler<HTMLInputElement>
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  dataTestId?: string
  textOnly?: boolean
  children?: ReactNode
  ariaLabel?: string
  ariaLabelledBy?: string
  ariaDescribedBy?: string
  autoFocus?: boolean
  afterLabelContent?: ReactNode
  labelClassName?: string
  additionalClassNames?: string
  // Presentational mode: keep the input out of the tab order and a11y tree when an outer control (e.g. a ListBoxItem) owns focus/keyboard/selection.
  tabIndex?: number
  ariaHidden?: boolean
}

const WrappedToggleInput = (props: Props, ref: React.RefObject<any>) => {
  const ariaLabelledBy = props.ariaLabelledBy ?? `${props.id}-label`
  const internalRef = React.useRef<HTMLInputElement | null>(null)
  const setInputRef = (node: HTMLInputElement | null) => {
    internalRef.current = node
    if (typeof ref === 'function') {
      ;(ref as (instance: HTMLInputElement | null) => void)(node)
    } else if (ref) {
      ;(ref as React.MutableRefObject<HTMLInputElement | null>).current = node
    }
  }

  const handleWrapperKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      event.target === internalRef.current &&
      (event.key === 'Enter' || event.key === ' ') &&
      !props.disabled
    ) {
      event.preventDefault()
      internalRef.current?.click()
    }
    if (props.onKeyDown) {
      props.onKeyDown(event as any)
    }
  }

  return (
    <div
      className={classnames('option', props.additionalClassNames)}
      onClick={props.onClick}
      onKeyDown={handleWrapperKeyDown}
    >
      <input
        ref={setInputRef}
        className={props.type}
        type={props.type}
        role={props.type}
        name={props.name}
        id={props.id}
        disabled={props.disabled}
        checked={props.checked}
        onChange={props.onChange}
        onClick={props.onClick}
        autoFocus={props.autoFocus}
        onKeyDown={props.onKeyDown}
        data-testid={props.dataTestId}
        tabIndex={props.tabIndex}
        aria-hidden={props.ariaHidden}
        aria-disabled={props.disabled}
        aria-label={props.ariaLabel}
        aria-labelledby={props.ariaHidden ? undefined : ariaLabelledBy}
        aria-describedby={props.ariaDescribedBy}
      />
      <label
        id={`${props.id}-label`}
        data-testid={`${props.id}-toggle-label`}
        className={classnames(
          'label',
          {
            showInput: !props.textOnly
          },
          props.labelClassName
        )}
        htmlFor={props.id}
      >
        <div className='labelContents'>{props.children}</div>
      </label>
      {props.afterLabelContent}
    </div>
  )
}

const ToggleInput = React.forwardRef(WrappedToggleInput)
export default ToggleInput
